import React, { memo, useEffect, useState } from 'react';
import { usePlayer } from '@/src/hooks/data/useLobby';
import useTourneyApiService from '@/src/hooks/utils/useTourneyApiService';
import { TOURNEY_TYPES } from '@/Poker/data/Constants';
import useGetAllTourneyTickets from '@/src/hooks/data/useGetAllTourneyTickets';
import useTourneyDetails from '@/src/hooks/utils/useTourneyDetails';
import {
  PSEUDO_EXPIRED_PLAYER,
  PSEUDO_LOCKED_PLAYER,
  TOAST_ERROR
} from '@/Poker/data/Constants';
import usePlatformCommunication from '@/src/hooks/utils/usePlatformCommunication';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '../ui/sheet';
import useGetSingleTourneyDefinitionById from '@/src/hooks/data/useGetSingleTourneyDefinitionById';

const Register = ({
  singleTourneyId,
  isLateReg = false,
  isLateTicket = false,
  isTicket = false
}) => {
  const tourneyDetails = useTourneyDetails();
  const { addCashNavigation } = usePlatformCommunication();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (tourneyDetails?.showTourneyRegisterConfirmationPopup === false) {
      setOpen(false);
    }
  }, [tourneyDetails?.showTourneyRegisterConfirmationPopup]);

  const user = usePlayer();
  const tourneyApi = useTourneyApiService();
  const getAllTourneyTickets = useGetAllTourneyTickets();

  const getTourneyBuyIn = () => {
    if (singleTourneyId?.bountyTourney) {
      return (
        singleTourneyId?.buyIn +
        singleTourneyId?.tourneyRake +
        singleTourneyId?.bountyBuyIn
      );
    } else {
      return singleTourneyId?.buyIn + singleTourneyId?.tourneyRake;
    }
  };

  const checkUserHasTicket = (tourneyId) => {
    let ticketTourneyIds = getAllTourneyTickets.map((eachTicket) => {
      return eachTicket.tourneyId;
    });
    return ticketTourneyIds.includes(tourneyId);
  };

  const handleTourneyRegister = () => {
    if (
      user?.player?.playerStatus === PSEUDO_LOCKED_PLAYER ||
      user?.player?.playerStatus === PSEUDO_EXPIRED_PLAYER
    ) {
      showToast(TOAST_ERROR, 'Player is restricted');
      return;
    } else {
      console.log('handleRegisterTourney clicked ');
      tourneyApi?.registerTourney(singleTourneyId?.tourneyId);
    }
  };

  return (
    <div
      onClick={(ev) => {
        ev.stopPropagation();
      }}
    >
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          {isTicket ? (
            <div
              onClick={() => tourneyDetails?.openRegPopup(true)}
              className="border-b-main rounded-200 flex h-[40px] min-w-[96px] flex-col items-center justify-center border border-solid text-xs"
            >
              <div className="text-success text-center text-xs font-bold">
                Ticket
              </div>
              <div className="text-success text-center text-xs font-bold">
                Available
              </div>
            </div>
          ) : (
            <div
              onClick={() => tourneyDetails?.openRegPopup(true)}
              className="border-b-main rounded-200 flex h-[40px] min-w-[96px] flex-col items-center justify-center border-1 border-solid text-xs"
            >
              <div className="text-secondary text-center text-xs font-medium">
                {isLateTicket ? (
                  <div>Ticket</div>
                ) : getTourneyBuyIn() === 0 ? (
                  <div>Free</div>
                ) : (
                  <div>{`₹${getTourneyBuyIn()}`}</div>
                )}
              </div>
              <div className="text-register text-center text-xs font-bold">
                {isLateReg ? 'Late Reg.' : 'Register'}
              </div>
            </div>
          )}
        </SheetTrigger>
        <SheetContent side="bottom" className={`bg-foreground`}>
          <div className="bg-foreground text-primary overflow-scroll rounded pt-2 pb-4 text-xs font-medium">
            <div className="text-gold-primary border-b-main border-b pb-2 text-center text-sm font-medium">
              Info
              <SheetClose>
                <div
                  onClick={() => tourneyDetails?.openRegPopup(false)}
                  className="absolute top-2 right-2 h-6 w-6"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 28 28"
                    fill="none"
                    // stroke="#fff"
                    className="stroke-primary"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </div>
              </SheetClose>
            </div>
            {user?.balance?.totalBalance < getTourneyBuyIn() &&
            !checkUserHasTicket(singleTourneyId?.tourneyId) ? (
              <div className="p-4">
                <div className="text-secondary font-regular mt-2 text-center text-xs">
                  {`You don't have enough balance to register for ${singleTourneyId?.tourneyName}. Please add funds to your account.`}
                </div>
                <div className="mt-4 flex items-center justify-center">
                  <div
                    className="text-light from-button-1 to-button-2 border-button-1 flex w-1/2 items-center justify-center rounded border bg-linear-to-b py-2 text-center text-sm font-bold"
                    onClick={() => {
                      setOpen(false);
                      tourneyDetails?.openRegPopup(false);
                      addCashNavigation(); // Redirect to add funds page or show add funds modal
                    }}
                  >
                    Add Cash
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4">
                <div className="text-primary text-center text-sm font-medium">
                  {`Do you want to register for ${singleTourneyId?.tourneyName} ?`}
                </div>
                <div className="text-secondary font-regular mt-2 text-center text-xs">
                  {singleTourneyId?.tournamentType === TOURNEY_TYPES.TICKET ||
                  checkUserHasTicket(singleTourneyId?.tourneyId)
                    ? ''
                    : `Amount : ₹${getTourneyBuyIn()}`}
                </div>
                <div className="mt-4 flex items-center justify-center">
                  <div className="text-button-1 border-button-1 mr-3 w-1/2 rounded border py-2 text-center text-sm font-bold">
                    {/* <DrawerClose> */}
                    <div
                      className="ml-1"
                      onClick={() => {
                        tourneyDetails?.openRegPopup(false);
                        setOpen(false);
                        // setRegisterTourneyModal(false);
                        //   tourneyDetails?.openRegPopup(false);
                      }}
                    >
                      No
                    </div>
                    {/* </DrawerClose> */}
                  </div>
                  <div
                    className="text-light from-button-1 to-button-2 border-button-1 flex w-1/2 items-center justify-center rounded border bg-linear-to-b py-2 text-center text-sm font-bold"
                    onClick={() => {
                      handleTourneyRegister();
                    }}
                  >
                    {/* {registerTourneyLoading ? <Loading /> :  */}
                    <div>Yes</div>
                    {/* } */}
                  </div>
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Register;
